/**
 * zkLogin authentication service for Coffee Rewards.
 * 
 * Flow:
 * 1. Generate ephemeral keypair + nonce
 * 2. Redirect to Google OAuth with nonce
 * 3. Handle callback — get JWT
 * 4. Derive zkLogin Sui address from JWT
 * 5. Get ZK proof (from Mysten Labs prover or self-hosted)
 * 6. User is now authenticated with a Sui address
 */
/// <reference types="vite/client" />
import { Buffer } from 'buffer';
import { Ed25519Keypair } from '@mysten/sui.js/keypairs/ed25519';
import { decodeSuiPrivateKey } from '@mysten/sui.js/cryptography';
import { generateNonce, generateRandomness, getExtendedEphemeralPublicKey, jwtToAddress } from '@mysten/zklogin';
import { suiClient } from './sui-client';
import { setSupabaseSession } from './supabase-auth';
import { setSessionId } from './api-client';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Dynamic redirect URI based on current domain
function getRedirectUri(): string {
    const currentOrigin = window.location.origin;
    return `${currentOrigin}/auth/callback`;
}

// Storage keys
const STORAGE_KEYS = {
    EPHEMERAL_KEYPAIR: 'coffee_rewards_ephemeral_keypair',
    RANDOMNESS: 'coffee_rewards_randomness',
    MAX_EPOCH: 'coffee_rewards_max_epoch',
    JWT: 'coffee_rewards_jwt',
    USER_ADDRESS: 'coffee_rewards_address',
    USER_SALT: 'coffee_rewards_salt',
    ZK_PROOF: 'coffee_rewards_proof',
    SUPABASE_JWT: 'coffee_rewards_supabase_jwt',
} as const;

export type OAuthProviderId = 'google' | 'apple';

/**
 * Step 1: Start the login flow — generate the ephemeral keypair, then hand off
 * to the provider.
 *
 * The ephemeral keypair and the nonce derived from it are IDENTICAL for both
 * providers: the nonce binds the keypair to whichever id_token comes back, and
 * the prover needs that binding regardless of who issued the token. Only the
 * authorize URL differs.
 *
 * Apple's URL is built SERVER-side (/api/auth/apple/start) rather than here,
 * for two reasons: the Services ID and redirect_uri must match Apple's records
 * byte-for-byte and are better held in one place, and the `state` parameter has
 * to be HMAC-signed with a server secret.
 */
export async function startOAuthLogin(
    provider: OAuthProviderId = 'google',
    dest?: string,
): Promise<void> {
    // Generate ephemeral keypair
    const keypair = new Ed25519Keypair();
    const randomness = generateRandomness();

    // Get the current epoch from Sui
    const { epoch } = await suiClient.getLatestSuiSystemState();
    const maxEpoch = Number(epoch) + 10; // Valid for ~10 epochs

    // Generate nonce from ephemeral public key
    // generateRandomness() returns string, but generateNonce needs BigInt
    const randomnessBigInt = BigInt(randomness);
    const nonce = generateNonce(keypair.getPublicKey(), maxEpoch, randomnessBigInt);

    // Store ephemeral keypair seed in localStorage (persists across browser restarts)
    // getSecretKey() returns Bech32 "suiprivkey1..." — decode to raw 32-byte seed,
    // then base64-encode for storage. fromSecretKey() expects exactly 32 bytes.
    const { secretKey: seedBytes } = decodeSuiPrivateKey(keypair.getSecretKey());
    localStorage.setItem(STORAGE_KEYS.EPHEMERAL_KEYPAIR, Buffer.from(seedBytes).toString('base64'));
    localStorage.setItem(STORAGE_KEYS.RANDOMNESS, randomness.toString());
    localStorage.setItem(STORAGE_KEYS.MAX_EPOCH, maxEpoch.toString());

    if (provider === 'apple') {
        const query = new URLSearchParams({ nonce, dest: dest || '/' });
        let res: Response;
        try {
            res = await fetch(`/api/auth/apple/start?${query.toString()}`);
        } catch {
            throw new Error('Cannot reach /api/auth/apple/start — check Vercel function deployment');
        }
        if (!res.ok) {
            throw new Error('Apple sign-in is not available right now');
        }
        const { url } = await res.json();
        if (!url) throw new Error('Apple sign-in is not available right now');
        window.location.href = url;
        return;
    }

    // Build Google OAuth URL using authorization code flow.
    //
    // We deliberately do NOT request offline access. It would hand us a
    // long-lived refresh token capable of minting Google access tokens for the
    // user, and we have no use for one — our own session (see _lib/verify-auth)
    // keeps people logged in. Storing a credential we never spend is pure
    // liability, and asking for it also forced Google's consent screen on every
    // single login. `select_account` alone still lets people choose an account.
    const params = new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID,
        redirect_uri: getRedirectUri(),
        response_type: 'code',
        scope: 'openid email profile',
        nonce: nonce,
        prompt: 'select_account',
    });

    // Redirect to Google
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/** @deprecated Use startOAuthLogin('google'). Kept so existing callers (Dashboard's re-login) need no change. */
export async function startGoogleLogin(): Promise<void> {
    return startOAuthLogin('google');
}

/**
 * Step 2: Handle the OAuth callback — extract authorization code and exchange for JWT
 */
export async function handleAuthCallback(): Promise<{
    address: string;
    jwt: string;
    salt: string;
    existingProvider?: string;
}> {
    // Extract authorization code from URL query string.
    //
    // Google lands here directly. Apple cannot — its form_post callback is a
    // cross-site POST that static hosting cannot receive — so the server
    // endpoint converts it into this same GET and forwards ?code plus
    // ?provider=apple and, on a first authorization only, ?au=<signed name>.
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDesc = params.get('error_description');
    const provider = params.get('provider') === 'apple' ? 'apple' : 'google';
    const appleUser = params.get('au');

    if (error) {
        const label = provider === 'apple' ? 'Apple' : 'Google';
        throw new Error(`${label} sign-in error: ${errorDesc || error}`);
    }

    if (!code) {
        throw new Error('No authorization code received from the sign-in provider');
    }

    // Exchange code for JWT with Vercel function
    let response: Response;
    try {
        response = await fetch('/api/auth/oauth', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                code,
                provider,
                // Apple's redirect_uri is server-held and must match the
                // authorize request byte-for-byte; the browser never saw it.
                ...(provider === 'apple'
                    ? { ...(appleUser ? { appleUser } : {}) }
                    : { redirectUri: getRedirectUri() }),
            }),
        });
    } catch (fetchErr: any) {
        throw new Error('Cannot reach /api/auth/oauth — check Vercel function deployment');
    }

    if (!response.ok) {
        let errorMsg = `Auth endpoint returned ${response.status}`;
        try {
            const errorData = await response.json();
            errorMsg = errorData.message || errorData.error || errorMsg;
        } catch { /* ignore parse error */ }
        throw new Error(`Token exchange failed: ${errorMsg}`);
    }

    const authResponseData = await response.json();
    const { jwt } = authResponseData;

    if (!jwt) {
        throw new Error('No JWT received from auth endpoint');
    }

    // Get salt — prefer inline salt from Google auth response, otherwise fetch separately
    const decodedJwt = decodeJwt(jwt);
    let salt: string;

    if (authResponseData.salt) {
        // Google auth endpoint returned salt inline — skip separate salt fetch
        salt = authResponseData.salt;
    } else {
        // Fall back to dedicated salt endpoint
        let saltResponse: Response;
        try {
            saltResponse = await fetch('/api/auth/salt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subject: decodedJwt.sub }),
            });
        } catch {
            throw new Error('Cannot reach /api/auth/salt — check Vercel function deployment');
        }

        if (!saltResponse.ok) {
            throw new Error('Could not retrieve salt from /api/auth/salt');
        }

        const { salt: serverSalt } = await saltResponse.json();
        salt = serverSalt;
    }

    // Normalize salt to numeric string (backend returns hex, jwtToAddress needs BigInt-compatible)
    const numericSalt = normalizeSalt(salt);

    // Derive the zkLogin Sui address
    const address = jwtToAddress(jwt, numericSalt);

    // Store everything
    localStorage.setItem(STORAGE_KEYS.JWT, jwt);
    localStorage.setItem(STORAGE_KEYS.USER_ADDRESS, address);
    localStorage.setItem(STORAGE_KEYS.USER_SALT, numericSalt);

    // Set Supabase session for SELECT/Realtime
    if (authResponseData.supabaseJwt) {
        await setSupabaseSession(authResponseData.supabaseJwt);
    }

    // Persist server-side session id used for /api/* writes
    if (authResponseData.sessionId) {
        setSessionId(authResponseData.sessionId, address);
    }

    return {
        address,
        jwt,
        salt: numericSalt,
        // Set only when an Apple signup shares an email with an existing
        // Google card. The two cards cannot be merged — see api/_lib/
        // existing-account.ts — so this is a heads-up, not a blocker.
        existingProvider: authResponseData.existingProvider,
    };
}

/**
 * Step 3: Get ZK proof via backend proxy (avoids Shinami CORS issues)
 */
export async function getZkProof(jwt: string, salt: string): Promise<any> {
    const ephemeralKeyBase64 = localStorage.getItem(STORAGE_KEYS.EPHEMERAL_KEYPAIR);
    const randomness = localStorage.getItem(STORAGE_KEYS.RANDOMNESS);
    const maxEpoch = localStorage.getItem(STORAGE_KEYS.MAX_EPOCH);

    if (!ephemeralKeyBase64 || !randomness || !maxEpoch) {
        throw new Error('Missing ephemeral keypair data. Please login again.');
    }

    const keypair = keypairFromStored(ephemeralKeyBase64);
    const extendedEphemeralPublicKey = getExtendedEphemeralPublicKey(keypair.getPublicKey());

    // Ensure salt is BigInt string (Shinami requires BigInt or Base64, not hex)
    const normalizedSalt = normalizeSalt(salt);

    // Route through Vercel function to avoid CORS issues with Shinami zkprover
    const response = await fetch('/api/zklogin/proof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            jwt,
            ephemeralPublicKey: extendedEphemeralPublicKey,
            maxEpoch: parseInt(maxEpoch),
            randomness,
            salt: normalizedSalt,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('ZK proof request failed:', errorText);
        throw new Error('Failed to get ZK proof from backend');
    }

    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'ZK proof generation failed');
    }

    const proof = data.proof;
    // Diagnostic: log the keys-only shape so we can spot Shinami schema drift
    // (e.g. headerBase64 going missing) without dumping PII into logs.
    try {
        console.info('[zklogin-auth] prover response shape:', JSON.stringify(describeProofShape(proof)));
    } catch { /* ignore */ }
    localStorage.setItem(STORAGE_KEYS.ZK_PROOF, JSON.stringify(proof));
    return proof;
}

function describeProofShape(p: any): any {
    if (p == null) return null;
    if (typeof p !== 'object') return typeof p;
    if (Array.isArray(p)) return { type: 'array', length: p.length };
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(p)) {
        if (v === '' || v == null) out[k] = `<empty:${v === '' ? 'string' : 'null'}>`;
        else if (Array.isArray(v)) out[k] = `array(len=${v.length})`;
        else if (typeof v === 'object') out[k] = describeProofShape(v);
        else out[k] = typeof v;
    }
    return out;
}

/**
 * Re-fetch a fresh ZK proof using the currently-stored JWT + salt. Used as an
 * auto-recovery path when the stored proof is malformed at sign time — avoids
 * making the customer log out + back in for a transient prover glitch.
 */
export async function refreshZkProof(): Promise<any> {
    const jwt = localStorage.getItem(STORAGE_KEYS.JWT);
    const salt = localStorage.getItem(STORAGE_KEYS.USER_SALT);
    if (!jwt || !salt) {
        throw new Error('Cannot refresh proof — JWT or salt missing. Please log in again.');
    }
    return getZkProof(jwt, salt);
}

/**
 * Reconstruct Ed25519Keypair from stored value.
 * Handles both formats: raw 32-byte seed (base64) and stale Bech32 "suiprivkey1..." strings.
 */
function keypairFromStored(stored: string): Ed25519Keypair {
    // If it looks like a Bech32 suiprivkey, decode it properly
    if (stored.startsWith('suiprivkey')) {
        const { secretKey } = decodeSuiPrivateKey(stored);
        return Ed25519Keypair.fromSecretKey(secretKey);
    }
    // Otherwise treat as base64-encoded 32-byte seed
    const bytes = Buffer.from(stored, 'base64');
    if (bytes.length !== 32) {
        throw new Error(`Expected 32-byte seed, got ${bytes.length}. Please log out and log in again.`);
    }
    return Ed25519Keypair.fromSecretKey(bytes);
}

/**
 * Get the stored ephemeral keypair (for signing transactions)
 */
export function getEphemeralKeypair(): Ed25519Keypair | null {
    const stored = localStorage.getItem(STORAGE_KEYS.EPHEMERAL_KEYPAIR);
    if (!stored) return null;
    try {
        return keypairFromStored(stored);
    } catch {
        return null;
    }
}

/**
 * Check if the stored JWT has expired (with 5-minute safety buffer)
 */
function isJwtExpired(): boolean {
    const jwt = localStorage.getItem(STORAGE_KEYS.JWT);
    if (!jwt) return false;
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const bufferMs = 5 * 60 * 1000; // 5-minute safety buffer
        return Date.now() >= (payload.exp * 1000 - bufferMs);
    } catch {
        return true;
    }
}

/**
 * Check if user is authenticated.
 *
 * The Google id_token expires after ~1h, but it is only needed to GENERATE a
 * zk proof — signing is gated on-chain by maxEpoch (~days), not the id_token.
 * So a stored proof keeps the session alive past id_token expiry; wiping auth
 * on `exp` here forced a re-login every ~55 minutes for no reason. Only when
 * there is NO proof (so a fresh proof fetch would need the id_token) does an
 * expired id_token actually end the session.
 */
export function isAuthenticated(): boolean {
    const hasCore = Boolean(
        localStorage.getItem(STORAGE_KEYS.USER_ADDRESS) &&
        localStorage.getItem(STORAGE_KEYS.EPHEMERAL_KEYPAIR)
    );
    if (!hasCore) return false;
    if (localStorage.getItem(STORAGE_KEYS.ZK_PROOF)) return true;
    if (isJwtExpired()) {
        clearAuth();
        return false;
    }
    return Boolean(localStorage.getItem(STORAGE_KEYS.JWT));
}

/**
 * Get stored auth data
 */
export function getAuthData() {
    return {
        address: localStorage.getItem(STORAGE_KEYS.USER_ADDRESS),
        jwt: localStorage.getItem(STORAGE_KEYS.JWT),
        salt: localStorage.getItem(STORAGE_KEYS.USER_SALT),
        proof: localStorage.getItem(STORAGE_KEYS.ZK_PROOF),
    };
}

/**
 * Friendly player handle from the Google id_token (scope includes `profile`).
 * Returns the first name where possible, else the full name, else null.
 * Used to show "PLAYER: Alex" instead of a raw hex address — hex addresses
 * break both the 8-bit fiction and the "it's just a loyalty card" promise.
 */
export function getDisplayName(): string | null {
    try {
        const jwt = localStorage.getItem(STORAGE_KEYS.JWT);
        if (!jwt) return null;
        const claims = decodeJwt(jwt);
        const name = claims?.given_name || claims?.name;
        return typeof name === 'string' && name.trim() ? name.trim() : null;
    } catch {
        return null;
    }
}

/**
 * Clear all auth data (logout)
 */
export function clearAuth(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });
}

// ============================================
// Helpers
// ============================================

function decodeJwt(jwt: string): any {
    const parts = jwt.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
}

/**
 * Normalize salt to a numeric string compatible with BigInt.
 * Backend returns hex (e.g. "91b0e3c2..."), but jwtToAddress needs a decimal number string.
 */
function normalizeSalt(salt: string): string {
    // If it's already a valid decimal number, return as-is
    if (/^\d+$/.test(salt)) {
        return salt;
    }
    // Otherwise treat as hex and convert to decimal BigInt string
    return BigInt('0x' + salt).toString();
}
